/**
 * PersonPicker — 담당 FP · WM 고르기 (이름을 치면 찾아서 넣고, 없으면 그 자리에서 바로 등록) · 2026-10-07 지도사
 *   「여기서 FP님, WM님 바로 등록하는 버튼도 하나 넣어주라 · 이름 쓰면 자동으로 찾아서 입력되는 기능도」
 *
 *   - 찾기: 이름 · 소속 · 연락처(끝 4자리) · 초성(「ㄱㅊㅅ」 → 김창숙). 이름이 딱 한 사람과 같으면 바로 고른다.
 *   - 키보드: ↑↓ 로 고르고 Enter, Esc 로 닫기.
 *   - 새로 등록: 목록 맨 아래 「＋ ○○ 새 FP 등록」 → 이름 · 연락처 · 소속만 받아 POST /api/tm/fps|wms → 바로 선택.
 *     같은 이름이 이미 있으면 등록 전에 그 자리에서 알려 준다(이분으로 선택 · 새로 등록 · 확인 창 없이).
 *   - 한글 조합(IME) 중에는 칸 글을 바꾸지 않는다 — 끝 글자를 다 친 뒤에 고른다(2026-10-26).
 *   - 쓰는 곳: 약속 수정 · 동행상담 등록 · 업체 등록 · 업체 상세(담당 FP 추가 · 담당 WM). 목록 모양은 한 벌.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FP, WM } from '../types';
import { C, T, R } from './ui';
import * as api from '../api';

type Person = Pick<FP, 'id' | 'name' | 'company' | 'phone'> | Pick<WM, 'id' | 'name' | 'company' | 'phone'>;

interface Props {
  kind: 'fp' | 'wm';
  people: Person[];
  value: string;                       // 고른 id · '' = 아직 안 고름 · noneValue = 없음
  onChange: (id: string) => void;
  onCreated?: (p: FP | WM) => void;    // 새로 등록한 사람 — 상위 목록에 붙이기
  noneValue?: string;                  // 주면 「없음」 줄을 보인다 (그 값으로 onChange)
  noneLabel?: string;
  exclude?: string[];                  // 이미 고른 사람(여러 명 담당 FP 추가할 때)
  placeholder?: string;
  size?: 'md' | 'sm';
  clearAfterPick?: boolean;            // 고른 뒤 칸을 비운다(여러 명 추가용)
  onToast?: (msg: string, type?: 'success' | 'error') => void;
}

const LABEL = { fp: 'FP', wm: 'WM' } as const;
const CHO = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
/** 한글 → 초성 줄 (「김창숙」 → 「ㄱㅊㅅ」) */
function choOf(s: string): string {
  let o = '';
  for (const ch of s.split('')) {
    const c = ch.charCodeAt(0) - 0xac00;
    o += c >= 0 && c < 11172 ? CHO[Math.floor(c / 588)] : ch;
  }
  return o;
}
const isCho = (s: string) => s.length > 0 && s.split('').every((ch) => CHO.includes(ch));
const digits = (s: string) => String(s || '').replace(/\D/g, '');
const fmtPhone = (s: string) => {
  const d = digits(s);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return s || '';
};
const labelOf = (p: Person) => `${p.name}${p.company ? ` (${p.company})` : ''}`;

export default function PersonPicker({ kind, people, value, onChange, onCreated, noneValue, noneLabel, exclude = [], placeholder, size = 'md', clearAfterPick, onToast }: Props) {
  const [extra, setExtra] = useState<Person[]>([]);            // 이 칸에서 막 등록한 사람(상위 목록이 갱신되기 전에도 보이게)
  const all = useMemo(() => {
    const ids = new Set(people.map((p) => p.id));
    return people.concat(extra.filter((p) => !ids.has(p.id)));
  }, [people, extra]);
  const cur = all.find((p) => p.id === value) || null;
  const shown = value && noneValue && value === noneValue ? (noneLabel || '없음') : cur ? labelOf(cur) : '';
  const [q, setQ] = useState(shown);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [adding, setAdding] = useState<null | { name: string; phone: string; company: string }>(null);
  const [dup, setDup] = useState<Person | null>(null);           // 새로 등록하려는 이름이 이미 있을 때 — 고르기 · 그래도 등록(브라우저 확인 창 대신)
  const [busy, setBusy] = useState(false);
  // 한글 조합 중(IME)에는 칸 글을 바꾸지 않는다 — 조합 중에 바꾸면 끝 글자가 한 번 더 붙었다
  //   (2026-10-26 「김상균 (한화생명금융서비스)균」 · 「＋ 새 WM 등록」으로 잘못 감)
  const [composing, setComposing] = useState(false);
  const comp = useRef(false);
  const inpRef = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);        // 펼친 목록 · 새 등록 칸
  const sureRef = useRef('');                      // 지금 친 이름과 딱 맞는 한 분(바깥을 누를 때 씀)

  // 밖에서 값이 바뀌면 보이는 글도 따라간다(열려 있을 때는 치는 글 유지)
  useEffect(() => { if (!open && !adding) setQ(clearAfterPick ? '' : shown); }, [shown, open, adding, clearAfterPick]);
  // 바깥을 누르면 닫는다 — 고르지 않고 닫으면 원래 값으로
  useEffect(() => {
    if (!open && !adding) return;
    const h = (e: MouseEvent) => {
      // composedPath — 누른 줄이 이 칸 안에서 막 사라졌어도(목록이 닫히며 지워짐) 안쪽으로 본다
      if (!box.current || box.current.contains(e.target as Node) || e.composedPath().includes(box.current)) return;
      if (open && sureRef.current && sureRef.current !== value) { pick(sureRef.current); setAdding(null); return; }   // 이름을 다 치고 바깥을 누르면 그 사람
      setOpen(false); setAdding(null); setDup(null); setQ(clearAfterPick ? '' : shown);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adding, shown, clearAfterPick, value]);

  const term = q.trim();
  const hits = useMemo(() => {
    const ex = new Set(exclude);
    const list = all.filter((p) => !ex.has(p.id));
    if (!term || term === shown) return list.slice(0, 50);
    const t = term.replace(/\s+/g, '');
    const d = digits(term);
    const cho = isCho(t);
    const score = (p: Person) => {
      const n = p.name.replace(/\s+/g, '');
      if (n === t) return 0;
      if (n.startsWith(t)) return 1;
      if (n.includes(t)) return 2;
      if (cho && choOf(n).includes(t)) return 3;
      if ((p.company || '').includes(term)) return 4;
      if (d.length >= 3 && digits(p.phone || '').includes(d)) return 5;
      return -1;
    };
    return list.map((p) => ({ p, s: score(p) })).filter((x) => x.s >= 0).sort((a, b) => a.s - b.s || a.p.name.localeCompare(b.p.name, 'ko')).map((x) => x.p).slice(0, 50);
  }, [all, term, shown, exclude]);
  const exact = term && term !== shown ? hits.filter((p) => p.name.replace(/\s+/g, '') === term.replace(/\s+/g, '')) : [];
  const canAdd = !!term && term !== shown && !isCho(term.replace(/\s+/g, '')) && digits(term).length < 3;
  // 줄 목록(키보드 이동용) — 없음 · 사람들 · 새로 등록
  type Row = { k: 'none' } | { k: 'p'; p: Person } | { k: 'add' };
  const rows: Row[] = [
    ...(noneValue && (!term || term === shown) ? [{ k: 'none' } as Row] : []),
    ...hits.map((p) => ({ k: 'p', p } as Row)),
    ...(canAdd ? [{ k: 'add' } as Row] : []),
  ];

  const pick = (id: string) => {
    if (comp.current) { comp.current = false; setComposing(false); inpRef.current?.blur(); }   // 조합을 먼저 끝낸 뒤 글을 바꾼다
    onChange(id);
    setOpen(false);
    const p = all.find((x) => x.id === id);
    setQ(clearAfterPick ? '' : id === noneValue ? (noneLabel || '없음') : p ? labelOf(p) : '');
  };
  const choose = (r: Row) => {
    if (r.k === 'none' && noneValue) pick(noneValue);
    else if (r.k === 'p') pick(r.p.id);
    else if (r.k === 'add') { setAdding({ name: term, phone: '', company: '' }); setOpen(false); }
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;   // 조합 중 Enter · ↑↓ 는 글자 확정용 — 끝난 뒤 다시 온다
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(rows.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (exact.length === 1) { pick(exact[0].id); return; }   // 이름을 다 쳤으면 그 사람
      if (open && rows[hi]) choose(rows[hi]);
    } else if (e.key === 'Escape') { setOpen(false); setQ(clearAfterPick ? '' : shown); }
  };
  // 이름을 다 쳐서 딱 한 사람과 같으면 바로 넣는다 (지도사 「이름 쓰면 자동으로 찾아서 입력」)
  //   단, 그 이름으로 시작하는 다른 사람이 있으면(「김창」 · 「김창숙」) 계속 치는 중일 수 있어 기다린다(Enter 로 고름)
  const sure = exact.length === 1 && hits.filter((p) => p.name.replace(/\s+/g, '').startsWith(term.replace(/\s+/g, ''))).length === 1 ? exact[0].id : '';
  sureRef.current = sure;
  //   한글은 끝 글자를 다 친 뒤(조합이 끝난 뒤)에 고른다 — 「김상규」를 치는 중인 「김상균」을 잘못 고르지 않게
  useEffect(() => {
    if (sure && sure !== value && open && !composing) pick(sure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sure, composing]);

  // 모달 아래쪽 칸(업체 등록의 담당 FP 등)은 목록이 스크롤 영역 밖으로 펼쳐져 안 보일 수 있다 — 펼칠 때 보이는 곳까지만 굴린다
  const isAdding = !!adding;
  useEffect(() => {
    if (open || isAdding) pop.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open, isAdding]);
  // ↑↓ 로 고른 줄이 목록 밖이면 따라 굴린다
  useEffect(() => {
    if (!open || !pop.current) return;
    const el = pop.current.querySelector<HTMLElement>(`[data-i="${hi}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [hi, open]);

  const save = async (force = false) => {
    if (!adding) return;
    const name = adding.name.trim();
    if (!name) { onToast?.('이름을 입력하세요', 'error'); return; }
    const same = all.find((p) => p.name.replace(/\s+/g, '') === name.replace(/\s+/g, '') && (!adding.phone || digits(p.phone || '') === digits(adding.phone)));
    if (same && !force) { setDup(same); return; }   // 이미 있는 분 — 아래에서 고르기 · 그래도 새로 등록
    setDup(null);
    setBusy(true);
    try {
      const body = { name, phone: digits(adding.phone), company: adding.company.trim() };
      const r = kind === 'fp' ? await api.createFp(body) : await api.createWm(body);
      const row = r.data;
      if (!row || !row.id) throw new Error('등록 실패');
      setExtra((x) => x.concat([row]));
      onCreated?.(row);
      setAdding(null);
      pick(row.id);
      onToast?.(`${LABEL[kind]} ${row.name}님을 등록했습니다`);
    } catch (e) { onToast?.(e instanceof Error ? e.message : '등록 실패', 'error'); }
    finally { setBusy(false); }
  };

  const h = size === 'sm' ? 30 : 38;
  const fs = size === 'sm' ? T.xs : T.md;
  const inp: React.CSSProperties = { width: '100%', height: h, padding: size === 'sm' ? '5px 30px 5px 8px' : '9px 34px 9px 12px', borderRadius: size === 'sm' ? R.sm : 8, border: `1px solid ${C.border}`, fontSize: fs, outline: 'none', background: '#fff', boxSizing: 'border-box' };
  const small: React.CSSProperties = { width: '100%', height: 34, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box', background: '#fff' };

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
      <input ref={inpRef} value={q} placeholder={placeholder || `${LABEL[kind]} 이름 · 소속 · 연락처로 찾기`} aria-label={`담당 ${LABEL[kind]}`}
        onCompositionStart={() => { comp.current = true; setComposing(true); }}
        onCompositionEnd={(e) => { comp.current = false; setComposing(false); setQ(e.currentTarget.value); }}
        onFocus={(e) => { setOpen(true); setHi(0); e.currentTarget.select(); }}
        onClick={(e) => { if (!open) { setOpen(true); setHi(0); e.currentTarget.select(); } }}   // 고른 뒤 다시 누르면 글 전체 선택 — 바로 다른 이름을 칠 수 있게
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); setAdding(null); }}
        onKeyDown={onKey} style={inp} autoComplete="off" />
      {/* 펼침 표시 · 비우기 */}
      {value && !clearAfterPick && !open
        ? <button type="button" aria-label="선택 지우기" title="선택 지우기" onClick={() => { onChange(''); setQ(''); }}
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 14 }}>✕</button>
        : <span aria-hidden="true" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: 10, pointerEvents: 'none' }}>▼</span>}
      </div>

      {open && (
        <div ref={pop} role="listbox" style={{ position: 'absolute', zIndex: 50, left: 0, right: 0, top: h + 4, background: '#fff', border: `1px solid ${C.border}`, borderRadius: R.md, boxShadow: '0 8px 24px rgba(15,23,42,.12)', maxHeight: 260, overflowY: 'auto' }}>
          {rows.length === 0 && <div style={{ padding: '10px 12px', fontSize: T.sm, color: C.muted }}>찾는 {LABEL[kind]}이 없습니다</div>}
          {rows.map((r, i) => {
            const on = i === hi;
            const base: React.CSSProperties = { padding: '8px 12px', cursor: 'pointer', fontSize: T.sm, background: on ? C.accentLt : '#fff', borderBottom: `1px solid ${C.bg}`, display: 'flex', alignItems: 'center', gap: 8 };
            if (r.k === 'none') return <div key="none" data-i={i} role="option" aria-selected={on} onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); choose(r); }} style={{ ...base, color: C.sub }}>{noneLabel || '없음'}</div>;
            if (r.k === 'add') return (
              <div key="add" data-i={i} role="option" aria-selected={on} onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                style={{ ...base, color: C.accent, fontWeight: 700, borderBottom: 0 }}>＋ 「{term}」 새 {LABEL[kind]} 등록</div>
            );
            const p = r.p;
            return (
              <div key={p.id} data-i={i} role="option" aria-selected={on} onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); choose(r); }} style={{ ...base, fontWeight: p.id === value ? 700 : 400 }}>
                {/* 좁은 칸(업체 상세 · 모바일)에서도 이름은 한 줄 — 소속이 길면 소속만 말줄임 */}
                <span style={{ fontWeight: 600, color: C.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{p.name}</span>
                {p.company && <span title={p.company} style={{ color: C.muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.company}</span>}
                {p.phone && <span style={{ marginLeft: 'auto', color: C.muted, fontSize: T.xs, whiteSpace: 'nowrap' }}>{fmtPhone(p.phone)}</span>}
              </div>
            );
          })}
          {!canAdd && (
            <div onMouseDown={(e) => { e.preventDefault(); setAdding({ name: term && term !== shown && !isCho(term) && digits(term).length < 3 ? term : '', phone: digits(term).length >= 3 ? term : '', company: '' }); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: T.sm, color: C.accent, fontWeight: 700 }}>＋ 새 {LABEL[kind]} 등록</div>
          )}
        </div>
      )}

      {adding && (
        <div ref={pop} style={{ marginTop: 6, padding: 10, borderRadius: R.md, border: `1px solid ${C.accent}`, background: C.accentLt }}>
          <div style={{ fontSize: T.xs, fontWeight: 700, color: C.accent, marginBottom: 6 }}>새 {LABEL[kind]} 등록</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 6 }}>
            <input autoFocus value={adding.name} placeholder="이름 *" aria-label="이름" onChange={(e) => { setAdding({ ...adding, name: e.target.value }); setDup(null); }} style={small}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); save(); } if (e.key === 'Escape') setAdding(null); }} />
            <input value={adding.phone} placeholder="연락처" aria-label="연락처" inputMode="tel" onChange={(e) => { setAdding({ ...adding, phone: e.target.value }); setDup(null); }} style={small}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); save(); } if (e.key === 'Escape') setAdding(null); }} />
            <input value={adding.company} placeholder="소속 (예: 한화생명금융서비스)" aria-label="소속" onChange={(e) => setAdding({ ...adding, company: e.target.value })} style={small}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); save(); } if (e.key === 'Escape') setAdding(null); }} />
          </div>
          {dup ? (
            <div role="alert" style={{ marginTop: 8, padding: '8px 10px', borderRadius: R.sm, background: '#fff', border: `1px solid ${C.orange}` }}>
              <div style={{ fontSize: T.xs, color: C.text, lineHeight: 1.5 }}>이미 등록된 분이 있어요. <b>{labelOf(dup)}</b>{dup.phone ? ` ${fmtPhone(dup.phone)}` : ''}</div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
                <button type="button" disabled={busy} onClick={() => save(true)} style={{ padding: '6px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.xs, cursor: busy ? 'default' : 'pointer', color: C.sub }}>다른 분이에요, 새로 등록</button>
                <button type="button" onClick={() => { const id = dup.id; setDup(null); setAdding(null); pick(id); }} style={{ padding: '6px 14px', borderRadius: R.sm, border: 0, background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>이분으로 선택</button>
              </div>
            </div>
          ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
            <button type="button" onClick={() => setAdding(null)} style={{ padding: '6px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.xs, cursor: 'pointer', color: C.sub }}>취소</button>
            <button type="button" disabled={busy} onClick={() => save()} style={{ padding: '6px 14px', borderRadius: R.sm, border: 0, background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? '등록 중…' : '등록하고 선택'}</button>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
